import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Seo, breadcrumbSchema, faqSchema, serviceSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionHead, FaqList, CtaBand, RelatedLinks, Img } from "@/components/Blocks";
import { WhatsAppIcon } from "@/components/Icon";
import { serviceBySlug, SERVICES, CATALOGUE } from "@/data/services";
import { INDUSTRY_PAGES } from "@/data/industries";
import { waLink } from "@/data/site";
import NotFound from "@/pages/NotFound";

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = serviceBySlug[slug];
  if (!s) return <NotFound />;
  const path = `/services/${s.slug}`;
  const crumbs = [["Home", "/"], ["Services", "/services"], [s.title, path]];
  const cat = CATALOGUE.find((c) => c.key === s.catalogue);
  const related = SERVICES.filter((x) => x.slug !== s.slug).slice(0, 12);
  const relSvc = [related[(SERVICES.indexOf(s) + 1) % related.length], related[(SERVICES.indexOf(s) + 4) % related.length], related[(SERVICES.indexOf(s) + 7) % related.length]].map((x) => x.slug);
  const relInd = INDUSTRY_PAGES.filter((i) => i.relServices.includes(s.slug)).slice(0, 3).map((i) => i.slug);
  const words = s.h1.split(" ");
  const mid = Math.ceil(words.length / 2);

  return (
    <>
      <Seo title={s.metaTitle} description={s.metaDesc} path={path} jsonLd={[breadcrumbSchema(crumbs), serviceSchema(s, path), faqSchema(s.faqs)]} />
      <PageHero crumbs={crumbs} eyebrow="Service" lines={[words.slice(0, mid).join(" "), words.slice(mid).join(" ")]} intro={s.intro} img={s.img} imgAlt={`${s.title} for industrial businesses`}>
        <Link to={`/contact?service=${encodeURIComponent(s.title)}#inquiry`} className="btn-primary" data-testid="service-hero-cta">Discuss Your Project <ArrowRight className="h-4 w-4" /></Link>
        <a href={waLink(`Hello Industrix Solution, I am interested in ${s.title}.`)} target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="service-hero-whatsapp"><WhatsAppIcon className="h-4 w-4" /> WhatsApp</a>
      </PageHero>

      <section className="wrap py-24 lg:py-32" data-testid="service-suitable">
        <div className="grid gap-14 lg:grid-cols-2">
          <SectionHead eyebrow="Who it is for" title="Suitable business types" lead={s.short} />
          <ul className="border-t border-charcoal/15">
            {s.suitable.map((t, i) => (
              <Reveal as="li" key={t} delay={i * 0.05} className="flex items-center gap-5 border-b border-charcoal/15 py-5">
                <Check className="h-4 w-4 shrink-0 text-rust-deep" /><span className="text-[16px]">{t}</span>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-stone bg-white py-24 lg:py-32" data-testid="service-features">
        <div className="wrap">
          <SectionHead eyebrow="Key features" title={`What ${s.title.toLowerCase()} can include`} lead="Features are selected and prioritised during discovery. Not every project needs every capability." />
          <div className="mt-14 grid gap-px overflow-hidden rounded-md border border-stone bg-stone md:grid-cols-2 lg:grid-cols-3">
            {s.features.map(([t, d], i) => (
              <Reveal key={t} delay={(i % 3) * 0.06} className="bg-white p-8">
                <span className="font-mono text-xs text-rust-ink">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-6 font-display text-xl font-bold">{t}</h3>
                <p className="mt-3 text-[14px] leading-relaxed text-graphite">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {cat && (
        <section className="wrap py-24 lg:py-32" data-testid="service-catalogue">
          <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <SectionHead eyebrow={`Catalogue ${cat.key}`} title={cat.title} />
              {cat.note && <Reveal><p className="mt-6 max-w-md rounded-md border-l-2 border-rust bg-white p-5 text-[14px] leading-relaxed text-graphite">{cat.note}</p></Reveal>}
              <Reveal className="relative mt-10 hidden aspect-[16/10] overflow-hidden rounded-md lg:block"><Img name={s.img} alt={`${s.title} in an industrial setting`} sizes="40vw" /></Reveal>
            </div>
            <ul className="grid content-start sm:grid-cols-2 sm:gap-x-8">
              {cat.items.map((it, i) => (
                <li key={it} className="flex gap-4 border-b border-charcoal/15 py-4 text-[15px]"><span className="font-mono text-xs text-rust-ink pt-1">{cat.key}{i + 1}</span>{it}</li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="relative overflow-hidden bg-charcoal py-24 text-ivory lg:py-32" data-testid="service-approach">
        <div className="grid-lines-dark absolute inset-0" aria-hidden="true" />
        <div className="wrap relative">
          <SectionHead light eyebrow="Implementation approach" title="How we deliver it" />
          <ol className="mt-14 grid gap-px overflow-hidden rounded-md border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-4">
            {s.approach.map((t, i) => (
              <Reveal as="li" key={t} delay={i * 0.08} className="bg-charcoal p-8">
                <span className="font-display text-5xl font-extrabold text-rust">{i + 1}</span>
                <p className="mt-6 text-[15px] leading-relaxed text-[#E4E0D6]">{t}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="wrap py-24 lg:py-32" data-testid="service-faq">
        <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHead eyebrow="FAQ" title={`${s.title}: common questions`} />
          <FaqList faqs={s.faqs} idPrefix="service-faq" />
        </div>
      </section>

      <RelatedLinks services={relSvc} industries={relInd} />
      <CtaBand title={`Plan your ${s.title.toLowerCase()} project`} text="Share your current process, systems and goals. We will review your requirements and suggest a practical scope and next steps." />
    </>
  );
}
